import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useRef, useState } from "react";
import { setColumnWidth } from "../../../helpers/cookieUtils";
import ListingHeader from "../listingHeader/ListingHeader";
import { PendingDeleteIcon } from "../../../modules/default-svg";
import userService from "../../../store/services/user.service";
import { generalActions } from "../../../store/actions";
import Spin from "../spinner/Spin";
import {
  getAccountCurrency,
  displayDecimalFormatted,
} from "../../../utils/systemSettings";
import { getCategoryName } from "../../../views/cashFlow/utils/categoryUtils";
import { BUDGET_COLUMNS } from "../../../helpers/columns";
import BudgetTable from "./ BudgetTable";
import { ColumnConfigProvider } from "./ColumnConfigProvider";

const BudgetCategoryTable = (props) => {
  const {
    columnsList,
    rowData,
    setIsCreate,
    searchHandler,
    tableName,
    dispatch,
    handleReminder,
    handleEdit,
    handleDelete,
    parentType,
    projectId,
    handleAddRow,
    budgetLoading,
  } = props;
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const setting = JSON.parse(localStorage.getItem("settings"));
  const tableContainerRef = useRef(null);
  const [editingHeader, setEditingHeader] = useState(null);
  const [headerValue, setHeaderValue] = useState("");
  const [columnSizing, setColumnSizing] = useState({});
  const [sorting, setSorting] = useState([]);
  const [colsList, setColumnsList] = useState([]);
  useEffect(() => {
    if (columnsList.length) {
      setColumnsList(columnsList);
    }
  }, [columnsList]);
  const [expandedRows, setExpandedRows] = useState({});
  const [isAllRowsExpanded, setIsAllRowsExpanded] = useState(false);
  const [budgetColumns] = useState(
    BUDGET_COLUMNS.map((col) => ({
      accessorKey: col.accessorKey,
      header: col.label,
      size: 160,
      isVisible: col.isVisible,
      isSortable: col.isSortable,
      columnType: col.columnType,
      columnId: col.columnId,
      order: col.order,
      label: col.label,
    }))
  );
  const handleHeaderDoubleClick = (columnId, currentHeader) => {
    setEditingHeader(columnId);
    setHeaderValue(currentHeader);
  };

  const handleHeaderSave = (columnId) => {
    if (!headerValue.trim()) {
      dispatch(
        generalActions.showToast({
          title: "Header name cannot be empty",
          type: "error",
        })
      );
      return;
    }

    const updatedColumnsDB = columnsList.map((col) => ({
      name: col.label,
      customName: col.accessorKey === columnId ? headerValue : col.customName,
      order: col.order,
      isVisible: col.isVisible,
    }));

    const payload = {
      Name: tableName,
      Columns: updatedColumnsDB,
    };

    userService
      .putUserLayoutRequest(payload)
      .then(() => {
        dispatch(
          generalActions.showToast({
            title: "Column header updated successfully",
            type: "success",
          })
        );
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Error updating column header",
            type: "error",
          })
        );
      })
      .finally(() => {
        setCols((prevCols) => {
          const newCols = prevCols.map((col) =>
            col.accessorKey === columnId ? { ...col, header: headerValue } : col
          );
          return newCols;
        });
      });

    setEditingHeader(null);
    setHeaderValue("");
  };

  const getSortingFunction = (col) => {
    switch (col.label) {
      case "Category":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.category || 0;
          const b = rowB.original.category || 0;
          return a - b;
        };

      case "Total Initial Budget":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalInitialBudgetCost) || 0;
          const b = parseFloat(rowB.original.totalInitialBudgetCost) || 0;
          return a - b;
        };

      case "Total Change Request":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalChangeRequestsCost) || 0;
          const b = parseFloat(rowB.original.totalChangeRequestsCost) || 0;
          return a - b;
        };

      case "Total Revised Budget":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalRevisedBudgetCost) || 0;
          const b = parseFloat(rowB.original.totalRevisedBudgetCost) || 0;
          return a - b;
        };
      case "Total Forecast Allocation":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalForecastAllocation) || 0;
          const b = parseFloat(rowB.original.totalForecastAllocation) || 0;
          return a - b;
        };
      case "Total Actual Hours":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalActualHours) || 0;
          const b = parseFloat(rowB.original.totalActualHours) || 0;
          return a - b;
        };
      case "Total Forecast Hours":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalForecastHours) || 0;
          const b = parseFloat(rowB.original.totalForecastHours) || 0;
          return a - b;
        };
      case "Total Forecast hourly Cost":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalForecastHourlyCostRate) || 0;
          const b = parseFloat(rowB.original.totalForecastHourlyCostRate) || 0;
          return a - b;
        };
      case "Total Forecast hourly Bill":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalForecastHourlyBillRate) || 0;
          const b = parseFloat(rowB.original.totalForecastHourlyBillRate) || 0;
          return a - b;
        };
      case "Total Forecast Cost":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalForecastCost) || 0;
          const b = parseFloat(rowB.original.totalForecastCost) || 0;
          return a - b;
        };
      case "Total Forecast Bill":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalForecastBill) || 0;
          const b = parseFloat(rowB.original.totalForecastBill) || 0;
          return a - b;
        };
      case "Total Forecast CV":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalForecastCv) || 0;
          const b = parseFloat(rowB.original.totalForecastCv) || 0;
          return a - b;
        };
      case "Total Forecast HV":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalForecastHv) || 0;
          const b = parseFloat(rowB.original.totalForecastHv) || 0;
          return a - b;
        };
      case "Total Invoiced":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalInvoiced) || 0;
          const b = parseFloat(rowB.original.totalInvoiced) || 0;
          return a - b;
        };

      case "Total Accural":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalAccural) || 0;
          const b = parseFloat(rowB.original.totalAccural) || 0;
          return a - b;
        };
      case "Total Spent":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalSpent) || 0;
          const b = parseFloat(rowB.original.totalSpent) || 0;
          return a - b;
        };

      case "Total PV":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalPV) || 0;
          const b = parseFloat(rowB.original.totalPV) || 0;
          return a - b;
        };

      case "Total EV":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalEV) || 0;
          const b = parseFloat(rowB.original.totalEV) || 0;
          return a - b;
        };

      case "Total ETC":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalETC) || 0;
          const b = parseFloat(rowB.original.totalETC) || 0;
          return a - b;
        };

      case "Total BAC":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalBAC) || 0;
          const b = parseFloat(rowB.original.totalBAC) || 0;
          return a - b;
        };

      case "Total CV":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalCV) || 0;
          const b = parseFloat(rowB.original.totalCV) || 0;
          return a - b;
        };

      case "Total SV":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.totalSV) || 0;
          const b = parseFloat(rowB.original.totalSV) || 0;
          return a - b;
        };

      default:
        return "alphanumeric";
    }
  };

  const getSortDescFirst = (col) => {
    const descFirstColumns = [
      "Total Invoiced",
      "Total Accural",
      "Total Spent",
      "Total PV",
      "Total EV",
      "Total ETC",
      "Total BAC",
      "Total CV",
      "Total SV",
    ];
    return descFirstColumns.includes(col.label);
  };

  const calculateTotals = useCallback((data) => {
    const totals = {
      id: 'total-row',
      category: 'Total',
      categoryName: 'Total',
      isTotal: true,
      totalInitialBudgetCost: 0,
      totalChangeRequestsCost: 0,
      totalRevisedBudgetCost: 0,
      totalForecastAllocation: 0,
      totalForecastHours: 0,
      totalForecastHourlyCostRate: 0,
      totalForecastHourlyBillRate: 0,
      totalForecastCost: 0,
      totalForecastBill: 0,
      totalForecastCv: 0,
      totalActual: 0,
      totalInvoiced: 0,
      totalAccural: 0,
      totalSpent: 0,
      totalPV: 0,
      totalEV: 0,
      totalETC: 0,
      totalBAC: 0,
      totalCV: 0,
      totalSV: 0,
      totalForecastHv: 0,
      totalActualHours: 0,
      items: []
    };

    data.forEach(item => {
      if (item.id !== 'total-row') {
        totals.totalInitialBudgetCost += parseFloat(item.totalInitialBudgetCost) || 0;
        totals.totalChangeRequestsCost += parseFloat(item.totalChangeRequestsCost) || 0;
        totals.totalRevisedBudgetCost += parseFloat(item.totalRevisedBudgetCost) || 0;
        totals.totalForecastAllocation += parseFloat(item.totalForecastAllocation) || 0;
        totals.totalForecastHours += parseFloat(item.totalForecastHours) || 0;
        totals.totalForecastCost += parseFloat(item.totalForecastCost) || 0;
        totals.totalForecastBill += parseFloat(item.totalForecastBill) || 0;
        totals.totalForecastCv += parseFloat(item.totalForecastCv) || 0;
        totals.totalActual += parseFloat(item.totalActual) || 0;
        totals.totalInvoiced += parseFloat(item.totalInvoiced) || 0;
        totals.totalAccural += parseFloat(item.totalAccural) || 0;
        totals.totalSpent += parseFloat(item.totalSpent) || 0;
        totals.totalPV += parseFloat(item.totalPV) || 0;
        totals.totalEV += parseFloat(item.totalEV) || 0;
        totals.totalETC += parseFloat(item.totalETC) || 0;
        totals.totalBAC += parseFloat(item.totalBAC) || 0;
        totals.totalCV += parseFloat(item.totalCV) || 0;
        totals.totalSV += parseFloat(item.totalSV) || 0;
        totals.totalForecastHv += parseFloat(item.totalForecastHv) || 0;
        totals.totalActualHours += parseFloat(item.totalActualHours) || 0;
      }
    });

    totals.totalForecastHourlyCostRate = totals.totalForecastHours > 0 ? parseFloat(totals.totalForecastCost) / parseFloat(totals.totalForecastHours) : 0;
    totals.totalForecastHourlyBillRate = totals.totalForecastHours > 0 ? parseFloat(totals.totalForecastBill) / parseFloat(totals.totalForecastHours) : 0;
    return totals;
  }, []);

  useEffect(() => {
    if (!colsList?.length) return;
    if (!colsList?.length && !rowData.length) return;
    const columns = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((col, index) => {
        return {
          Label: col.label,
          CustomName: col.customName,
          Order: col.order,
          Visibility: col.isVisible,
          accessorKey: col.accessorKey,
          header: col.header,
          enableHiding: true,
          enableColumnActions: false,
          grow: true,
          enableResizing: true,
          size: col.size,
          editVariant: col.columnType,
          enableGlobalFilter: true,
          enableSorting: true,
          sortingFn: getSortingFunction(col),
          sortDescFirst: getSortDescFirst(col),
          Cell: ({ cell, row }) => {
            const item = row.original;
            const isTotalRow = item.isTotal || item.id === 'total-row';
            const isTotalRowStyling = { backgroundColor: '#ffc000', fontWeight: 'bold' };

            switch (col.label) {
              case "Category":
                return (
                  isTotalRow ?
                    <div
                      className="flex items-center w-100 px-2 h-[28px] justify-end"
                      style={isTotalRowStyling}
                    >
                      Total
                    </div>
                    :
                    <div
                      className="d-flex justify-content-between align-items-center inputWidth"
                    >
                      <div className="d-flex align-items-center inputWidth">
                        <span className={`d-flex w-full text-truncate`}>
                          {item.isPendingDelete && (
                            <div className="mr-1">
                              <PendingDeleteIcon />
                            </div>
                          )}
                          {getCategoryName(item.category)}
                        </span>
                      </div>
                    </div>
                );
              case "Total Initial Budget":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalInitialBudgetCost)}
                  </div>
                );
              case "Total Change Request":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalChangeRequestsCost)}
                  </div>
                );
              case "Total Revised Budget":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalRevisedBudgetCost)}
                  </div>
                );
              case "Total Forecast Allocation":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {displayDecimalFormatted(setting, item.totalForecastAllocation)}
                  </div>
                );
              case "Total Actual Hours":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {displayDecimalFormatted(setting, item.totalActualHours)}
                  </div>
                );
              case "Total Forecast Hours":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {displayDecimalFormatted(setting, item.totalForecastHours)}
                  </div>
                );
              case "Total Forecast hourly Cost":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalForecastHourlyCostRate)}
                  </div>
                );
              case "Total Forecast hourly Bill":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalForecastHourlyBillRate)}
                  </div>
                );
              case "Total Forecast Cost":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalForecastCost)}
                  </div>
                );
              case "Total Forecast Bill":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalForecastBill)}
                  </div>
                );
              case "Total Forecast CV":
                return (
                  <div
                    className={`flex items-center w-100 px-2 h-[28px] ${(Number(item.totalForecastCv) < 0 ? "RedFieldBg" : "GreenFieldBg")}`}
                    style={isTotalRow ? { fontWeight: 'bold' } : {}}
                  >
                    {getAccountCurrency(setting)}
                    {displayDecimalFormatted(setting, item.totalForecastCv)}
                  </div>
                );
              case "Total Forecast HV":
                return (
                  <div
                    className={`flex items-center w-100 px-2 h-[28px] ${(Number(item.totalForecastHv) < 0 ? "RedFieldBg" : "GreenFieldBg")}`}
                    style={isTotalRow ? { fontWeight: 'bold' } : {}}
                  >
                    {displayDecimalFormatted(setting, item.totalForecastHv)}
                  </div>
                );
              case "Total Actual":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalActual)}
                  </div>
                );
              case "Total Invoiced":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, Number(item.totalInvoiced))}
                  </div>
                );
              case "Total Accural":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalAccural)}
                  </div>
                );
              case "Total Spent":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalSpent)}
                  </div>
                );
              case "Total PV":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalPV)}
                  </div>
                );
              case "Total EV":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalEV)}
                  </div>
                );
              case "Total ETC":
                return (
                  <div
                    className="flex items-center w-100 px-2 h-[28px]"
                    style={isTotalRow ? isTotalRowStyling : {}}
                  >
                    {getAccountCurrency(setting)} {displayDecimalFormatted(setting, item.totalETC)}
                  </div>
                );
              case "Total BAC":
                return (
                  <div
                    className={`flex items-center w-100 px-2 h-[28px] 
                      ${(Number(item.totalRevisedBudgetCost) < Number(item.totalBAC) ? "RedFieldBg" : "GreenFieldBg")
                      }`}
                    style={isTotalRow ? { fontWeight: 'bold' } : {}}
                  >
                    {getAccountCurrency(setting)}
                    {displayDecimalFormatted(setting, item.totalBAC)}
                  </div>
                );
              case "Total CV":
                return (
                  <div
                    className={`flex items-center w-100 px-2 h-[28px] ${(Number(item.totalRevisedBudgetCost) < Number(item.totalBAC) ? "RedFieldBg" : "GreenFieldBg")
                      }`}
                    style={isTotalRow ? { fontWeight: 'bold' } : {}}
                  >
                    {getAccountCurrency(setting)}
                    {displayDecimalFormatted(setting, item.totalCV)}
                  </div>
                );
              default:
                return (
                  <div style={isTotalRow ? isTotalRowStyling : {}}>
                    {cell.getValue()}
                  </div>
                );
            }
          },
          Header: ({ column }) => {
            return editingHeader === column.id ? (
              <input
                value={headerValue}
                onChange={(e) => setHeaderValue(e.target.value)}
                onBlur={() => handleHeaderSave(column.id)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleHeaderSave(column.id);
                  }
                }}
                autoFocus
                className="header-edit-input"
                style={{
                  minWidth: column.getSize() - 60,
                }}
              />
            ) : (
              <span>{column.columnDef.header}</span>
            );
          },
        };
      });
    setCols((prevCols) => {
      if (JSON.stringify(prevCols) !== JSON.stringify(columns)) {
        return columns;
      }
      return prevCols;
    });

    // Add total row to the data
    const dataWithTotals = [...rowData];
    if (rowData.length > 0) {
      const totalRow = calculateTotals(rowData);
      dataWithTotals.push(totalRow);
    }

    setRows(dataWithTotals);
  }, [colsList, rowData, editingHeader, headerValue, calculateTotals]);

  const BudgetDetailPanel = ({ rowItem, dispatch }) => {
    const [budgets, setBudgets] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const getBudget = async () => {
        try {
          setLoading(true);
          setBudgets(rowItem);
        } catch (error) {
          dispatch(
            generalActions.showNotificationToast({
              title: "Failed to fetch budgets",
              type: "error",
            })
          );
          setBudgets([]);
        } finally {
          setLoading(false);
        }
      };

      getBudget();
    }, [rowItem, dispatch]);

    if (loading) {
      return <div className="p-4 text-center w-full">Loading budgets...</div>;
    }

    return (
      <ColumnConfigProvider
        tableName={`${parentType ? parentType + '-' : ''}Budget-Category`}
      >
        <BudgetTable
          isCategoryParent={true}
          columnsList={budgetColumns}
          rowData={budgets}
          dispatch={dispatch}
          tableName={`${parentType ? parentType + '-' : ''}Budget-Category`}
          handleReminder={handleReminder}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          parentType={parentType}
        />
      </ColumnConfigProvider>
    );
  };

  // Memoize the renderDetailPanel function
  const renderDetailPanel = useCallback(
    ({ row }) => (
      <div
        style={{
          padding: "0 0.5rem",
          borderLeft: "2px solid #342c82",
          backgroundColor: "#f9f9fb",
          width: "100%",
        }}
      >
        <BudgetDetailPanel
          rowItem={row.original.items.length > 0 ? row.original.items : []}
          dispatch={dispatch}
        />
      </div>
    ),
    [dispatch]
  );

  const table = useMaterialReactTable({
    columns: cols,
    data: rows,
    enableExpanding: (row) => row.original.items > 0 && !row.original.isTotal,
    getRowCanExpand: (row) => row.original.items > 0 && !row.original.isTotal,
    ...(Object.keys(expandedRows).length > 0 && {
      renderDetailPanel: renderDetailPanel,
    }),
    enableHiding: true,
    enableSorting: true,
    manualSorting: false,
    visibleInShowHideMenu: false,
    enablePagination: false,
    enableTopToolbar: true,
    enableBottomToolbar: false,
    enableGlobalFilter: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    enableColumnDragging: false,
    enableColumnOrdering: false,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    editDisplayMode: "cell",
    globalFilterFn: "contains",
    enableEditing: false,
    enableFilters: false,
    enableCreatingRow: true,
    createDisplayMode: "row",
    positionCreatingRow: "bottom",
    initialState: {
      expanded: expandedRows,
      density: "compact",
      showGlobalFilter: false,
      showColumnFilters: false,
      enableHiding: false,
    },
    state: {
      sorting,
      isLoading: budgetLoading,
      columnSizing,
      showLoadingOverlay: false,
      expanded: expandedRows,
    },
    onSortingChange: setSorting,
    muiExpandButtonProps: ({ row, table }) => ({
      onClick: (e) => {
        e.stopPropagation();
        const isCurrentlyExpanded = expandedRows[row.id];
        setExpandedRows((prev) => ({
          ...prev,
          [row.id]: !isCurrentlyExpanded,
        }));
      },
      disabled: row.original?.items?.length === 0 || row.original.isTotal,
      sx: {
        visibility: row.original?.items?.length > 0 && !row.original.isTotal ? "visible" : "hidden",
        opacity: row.original?.items?.length > 0 && !row.original.isTotal ? 1 : 0.3,
      },
    }),
    muiExpandAllButtonProps: ({ table }) => ({
      onClick: (e) => {
        e.stopPropagation();
        const newExpandedRows = !isAllRowsExpanded
          ? table.getRowModel().rows.reduce((acc, row) => {
            acc[row.id] = row?.original?.items?.length > 0;
            return acc;
          }, {})
          : {};

        setExpandedRows(newExpandedRows);
        setIsAllRowsExpanded(!isAllRowsExpanded);
      },
      disabled: false
    }),
    positionGlobalFilter: "left",
    onColumnSizingChange: (updater) => {
      setColumnSizing((prev) => {
        const newSizing =
          typeof updater === "function" ? updater(prev) : updater;
        Object.entries(newSizing).forEach(([columnId, width]) => {
          setColumnWidth(tableName, columnId, width);
        });
        return newSizing;
      });
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        fontFamily: "Figtree-Medium",
        fontSize: "14px",
        padding: "1rem 0.5rem 1rem 1rem",
        backgroundColor: "#f8f7fc",
        color:"#342c82",
        height: "40px",
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "#ece8fd",
          "& .MuiDivider-root": {
            opacity: 1,
          },
          "& .MuiButtonBase-root": {
            opacity: 1,
          },
          fontWeight: 700,
        },
        position: "relative",
        "& .Mui-TableHeadCell-Content": {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
        },
        "& .MuiTableSortLabel-root": {
          marginLeft: "8px",
          paddingTop: "3px",
          opacity: 0,
          "&:hover": {
            opacity: 1,
          },
        },
        "& .MuiDivider-root": {
          cursor: "col-resize",
          backgroundColor: "#342C82",
          opacity: 0,
        },
        "& .MuiButtonBase-root": {
          cursor: "grab",
          opacity: 0,
          "& .MuiSvgIcon-root": {
            path:{
              fill: "#342C82"
            }
          }
        },
        ...(column.id === "mrt-row-expand" && {
          maxWidth: "40px",
          width: "40px",
          minWidth: "40px",
          padding: "1rem 0.5rem ",
        }),
      },
      onDoubleClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleHeaderDoubleClick(column.id, column.columnDef.header);
      },
    }),
    muiTopToolbarProps: {
      showColumnFilters: false,
      showGlobalFilter: true,
      enableHiding: false,
      sx: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        "& .MuiBox-root": {
          padding: 0,
          display: "flex",
          alignItems: "center",
        },
      },
    },
    renderTopToolbar: () => (
      <ListingHeader
        isHeader={true}
        rightPane={true}
        parentType="Project"
        type="Budget"
        btnLable="Add Budget"
        searchedData={searchHandler}
        setIsCreate={() => setIsCreate(true)}
        projectId={projectId}
        isCreatable={false}
        isSearchDisable={cols.length}
        handleCreateFunc={() => {
          handleAddRow();
          table.setCreatingRow(true);
        }}
      />
    ),
    muiTableContainerProps: {
      sx: {
        position: "relative",
        maxHeight: 'none',
        height: '100%',
        zIndex: 0,
        "& .MuiTable-root": {
          borderCollapse: "separate",
        },
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#342C82", // Your primary color
          borderRadius: "8px",
          "&:hover": {
            background: "#2a2269", // Darker shade on hover
          },
        },
        "&::-webkit-scrollbar-corner": {
          background: "#f1f1f1",
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",

        //marginBottom: "66px",
        "& .MuiTableBody-Cell": {
          "&:hover": {
            backgroundColor: "#ece8fd15",
          },
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100% !important",
        display: "flex",
        flexDirection: "column",
      }
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        ...(expandedRows[row.id]
          ? {
            backgroundColor: "#fefefe",
            "& td": {
              verticalAlign: "top",
            },
          }
          : {}),
        ...(row.original.isTotal || row.original.id === 'total-row'
          ? {
            backgroundColor: "#ffc000 !important",
            fontWeight: "bold !important",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
            borderTop: "2px solid #d4a017",
            "& td": {
              backgroundColor: "#ffc000 !important",
              fontWeight: "bold !important",
              "&:hover": {
                fontFamily: "Figtree-Medium !important",
                fontWeight: "bolder !important",
                backgroundColor: "#ffc000 !important"
              },
              padding: 0,
            },
          }
          : {}),
      },
      className: "hoverable-row",
    }),
    muiTableBodyCellProps: ({ cell, column, table, row }) => ({
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",
        padding: "0px 1rem",
        position: "relative",
        overflow: "visible",
        "div": {
          ...(row.original.isTotal || row.original.id === 'total-row'
            ? {
              paddingLeft: "1.5rem !important",
            }
            : {}),
        },
        "&:focus": {
          outline: "none",
        },
        "&:hover": {
          ...(row.original.isTotal || row.original.id === 'total-row'
            ? {
              fontFamily: "Figtree-Medium !important",
              fontWeight: "bolder !important",
              fontSize: "14px !important",
            }
            : {
              fontFamily: "Figtree-Medium",
              fontWeight: "bold",
              fontSize: "14px !important",
            }),
        },
        ...(column.id === "mrt-row-expand" && {
          maxWidth: "40px",
          width: "40px",
          minWidth: "40px",
          padding: "0px 0.5rem",
          visibility: row.original.isTotal || row.original.id === 'total-row' ? "hidden" : "visible",
        }),
      },
    }),
    renderEmptyRowsFallback: () => {
      return budgetLoading ? (
        <div className="d-flex justify-content-center">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="h-full flex flex-col items-center justify-center min-h-[400px] p-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="65"
              height="64"
              viewBox="0 0 65 64"
              fill="none"
              className="mb-4"
            >
              <path
                d="M6.9002 21.6008C6.9002 16.7407 10.8401 12.8008 15.7002 12.8008H49.3002C54.1603 12.8008 58.1002 16.7407 58.1002 21.6008V42.4008C58.1002 47.2609 54.1603 51.2008 49.3002 51.2008H32.8884L27.8968 46.2092C28.7964 44.3293 29.3002 42.2239 29.3002 40.0008C29.3002 32.0479 22.8531 25.6008 14.9002 25.6008C11.94 25.6008 9.18835 26.494 6.9002 28.0257V21.6008ZM14.9002 51.2008C17.416 51.2008 19.7381 50.3713 21.6079 48.9708L29.7688 57.132C30.3936 57.7569 31.4067 57.7569 32.0315 57.1321C32.6564 56.5072 32.6564 55.4942 32.0316 54.8693L23.8706 46.708C25.2708 44.8384 26.1002 42.5164 26.1002 40.0008C26.1002 33.8152 21.0858 28.8008 14.9002 28.8008C8.71461 28.8008 3.7002 33.8152 3.7002 40.0008C3.7002 46.1864 8.71461 51.2008 14.9002 51.2008ZM14.9002 48.0008C10.4819 48.0008 6.9002 44.4191 6.9002 40.0008C6.9002 35.5825 10.4819 32.0008 14.9002 32.0008C19.3185 32.0008 22.9002 35.5825 22.9002 40.0008C22.9002 44.4191 19.3185 48.0008 14.9002 48.0008Z"
                fill="#342C82"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No data currently available!
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              New entries will appear here once created.
            </p>
          </div>
        </div>
      ) : null;
    },
  });

  return <MaterialReactTable table={table} />;
};

export default BudgetCategoryTable;
